import { useCallback, useEffect, useState } from 'react';

import { fetchRelatedLists } from '../services/itemRelationships';
import { normalizeId } from '../utils/ids';

const EMPTY = Object.freeze([]);

/**
 * The related lists an item shows, with their counts (KPR-719): one request
 * per item, from `GET /api/items/{id}/related-lists`.
 *
 *   const { lists, loading, hasData, error, retry } = useRelatedLists({ itemId, enabled, refreshKey });
 *
 * - `enabled` false asks nothing and answers an empty `lists`: a card that
 *   defines no list never costs a request.
 * - `hasData`: the answer for THIS item has landed. Moving to another item
 *   drops the previous item's lists at once (they are never drawn under it)
 *   and reads again; a `refreshKey` change (something was related elsewhere)
 *   or `retry` reads again WITHOUT dropping what is shown, so the switcher
 *   never blanks for a revalidation.
 * - A 404 (the item is gone, or no longer the caller's) is an answer: no
 *   lists. The section around the switcher has its own error for that.
 * - Any other failure is `error`; what was on screen stays, `retry` asks again.
 * - A read the item moved on from is cancelled; its answer never lands.
 */
export default function useRelatedLists({ itemId, enabled = true, refreshKey = 0 }) {
	const id = normalizeId(itemId) || null;
	const active = Boolean(enabled && id && id !== '-1');
	// The answer, tied to the item it answers.
	const [answer, setAnswer] = useState({ itemId: null, lists: [] });
	const [pending, setPending] = useState(false);
	const [failed, setFailed] = useState(false);
	const [attempt, setAttempt] = useState(0);

	useEffect(() => {
		if (!active) {
			setPending(false);
			setFailed(false);
			return undefined;
		}
		const controller = new AbortController();
		setPending(true);
		setFailed(false);
		fetchRelatedLists(id, { signal: controller.signal })
			.then((lists) => {
				if (controller.signal.aborted) return;
				setAnswer({ itemId: id, lists });
				setPending(false);
			})
			.catch((error) => {
				if (controller.signal.aborted) return;
				if (error?.response?.status === 404) {
					setAnswer({ itemId: id, lists: [] });
					setPending(false);
					return;
				}
				console.error(error);
				setFailed(true);
				setPending(false);
			});
		return () => controller.abort();
	}, [active, id, refreshKey, attempt]);

	const retry = useCallback(() => setAttempt(n => n + 1), []);

	const hasData = active && answer.itemId === id;
	return {
		lists: hasData ? answer.lists : EMPTY,
		loading: active && pending,
		hasData,
		error: active && failed,
		retry,
	};
}
